import { HttpClient } from "@utils/index";
import type {
  CostSummaryResponse,
  CostSummaryViewModel,
  CostTimeSeriesResponse,
  CostTimeSeriesViewModel,
  CostBreakdownResponse,
  CostBreakdownViewModel,
  CostFilterParams,
  CostGranularity,
  CostDimension,
} from "@models/cost";
import type {
  ExternalRefUsageItemResponse,
  ExternalRefUsageItemViewModel,
  ExternalRefUsageFilterParams,
  MonitoringMappingJobViewModel,
  MonitoringProcessViewModel,
  MonitoringUsageItemResponse,
  CostEventResponse,
  CostEventViewModel,
  ExternalRefCapabilityUsageResponse,
  ExternalRefCapabilityUsageViewModel,
} from "@models/monitoring";
import {
  buildCostSummaryViewModel,
  buildCostTimeSeriesViewModel,
  buildCostBreakdownViewModel,
} from "@utils/builders/cost";
import {
  buildPaginatedExternalRefUsageViewModel,
  buildCostEventViewModel,
  buildExternalRefCapabilityUsageViewModel,
  buildMonitoringMappingJobViewModelFromUsage,
  buildMonitoringProcessViewModelFromUsage,
} from "@utils/builders/monitoring";
import type { PaginatedResponse } from "@models/common";

const BASE_URL = import.meta.env.VITE_XPLATFORM_CORE_API_BASE_URL;

/**
 * Convert a date string to a date-only string (YYYY-MM-DD).
 */
function toDateOnly(dateStr: string): string {
  return dateStr.slice(0, 10);
}

if (!BASE_URL) {
  throw new Error(
    "Missing required environment variable: VITE_XPLATFORM_CORE_API_BASE_URL. " +
      "Please check your .env file and ensure the XPlatform Core API URL is set.",
  );
}

class CostApi {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient(`${BASE_URL}`);
  }

  private appendCostFilters(
    params: URLSearchParams,
    filters?: CostFilterParams,
  ) {
    if (filters?.moduleName) params.append("module_name", filters.moduleName);
    if (filters?.serviceName)
      params.append("service_name", filters.serviceName);
    if (filters?.userId) params.append("user_id", filters.userId);
    if (filters?.organisationId)
      params.append("organisation_id", filters.organisationId);
    if (filters?.providerId) params.append("provider_id", filters.providerId);
    if (filters?.startDate)
      params.append("start_date", toDateOnly(filters.startDate));
    if (filters?.endDate)
      params.append("end_date", toDateOnly(filters.endDate));
  }

  async getCostSummary(
    filters?: CostFilterParams,
  ): Promise<CostSummaryViewModel> {
    const params = new URLSearchParams();
    this.appendCostFilters(params, filters);

    const queryString = params.toString();
    const url = `/cost-events/summary${queryString ? `?${queryString}` : ""}`;

    const response =
      await this.httpClient.makeAuthenticatedRequest<CostSummaryResponse>(url, {
        method: "GET",
      });
    return buildCostSummaryViewModel(response);
  }

  async getCostTimeSeries(
    granularity: CostGranularity,
    startDate: string,
    endDate: string,
    filters?: Omit<CostFilterParams, "startDate" | "endDate">,
  ): Promise<CostTimeSeriesViewModel> {
    const params = new URLSearchParams({
      granularity,
      start_date: toDateOnly(startDate),
      end_date: toDateOnly(endDate),
    });
    if (filters?.serviceName)
      params.append("service_name", filters.serviceName);
    if (filters?.userId) params.append("user_id", filters.userId);
    if (filters?.organisationId)
      params.append("organisation_id", filters.organisationId);
    if (filters?.providerId) params.append("provider_id", filters.providerId);

    const response =
      await this.httpClient.makeAuthenticatedRequest<CostTimeSeriesResponse>(
        `/cost-events/chart/time-series?${params.toString()}`,
        { method: "GET" },
      );
    return buildCostTimeSeriesViewModel(response);
  }

  async getCostBreakdown(
    dimension: CostDimension,
    filters?: CostFilterParams,
  ): Promise<CostBreakdownViewModel> {
    const params = new URLSearchParams();
    this.appendCostFilters(params, filters);

    const queryString = params.toString();
    const url = `/cost-events/breakdown/${dimension}${queryString ? `?${queryString}` : ""}`;

    const response =
      await this.httpClient.makeAuthenticatedRequest<CostBreakdownResponse>(
        url,
        { method: "GET" },
      );
    return buildCostBreakdownViewModel(response);
  }

  async getExternalRefUsage(
    filters?: ExternalRefUsageFilterParams,
  ): Promise<PaginatedResponse<ExternalRefUsageItemViewModel>> {
    const params = new URLSearchParams();
    if (filters?.limit !== undefined)
      params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined)
      params.append("offset", filters.offset.toString());
    if (filters?.serviceName)
      params.append("service_name", filters.serviceName);
    if (filters?.organisationId)
      params.append("organisation_id", filters.organisationId);
    if (filters?.startDate)
      params.append("start_date", toDateOnly(filters.startDate));
    if (filters?.endDate)
      params.append("end_date", toDateOnly(filters.endDate));

    const queryString = params.toString();
    const url = `/cost-events/external-ref-usage${queryString ? `?${queryString}` : ""}`;

    const response = await this.httpClient.makeAuthenticatedRequest<
      PaginatedResponse<ExternalRefUsageItemResponse>
    >(url, { method: "GET" });
    return buildPaginatedExternalRefUsageViewModel(response);
  }

  private async getMonitoringUsage(
    service: string,
    filters?: ExternalRefUsageFilterParams,
  ): Promise<PaginatedResponse<MonitoringUsageItemResponse>> {
    const params = new URLSearchParams({ service });
    if (filters?.limit !== undefined)
      params.append("limit", filters.limit.toString());
    if (filters?.offset !== undefined)
      params.append("offset", filters.offset.toString());
    if (filters?.organisationId)
      params.append("organisation_id", filters.organisationId);
    if (filters?.startDate)
      params.append("start_date", toDateOnly(filters.startDate));
    if (filters?.endDate)
      params.append("end_date", toDateOnly(filters.endDate));

    return this.httpClient.makeAuthenticatedRequest<
      PaginatedResponse<MonitoringUsageItemResponse>
    >(`/cost-events/monitoring/usage?${params.toString()}`, { method: "GET" });
  }

  async getMappingUsage(
    filters?: ExternalRefUsageFilterParams,
  ): Promise<PaginatedResponse<MonitoringMappingJobViewModel>> {
    const response = await this.getMonitoringUsage("mapping", filters);
    return {
      ...response,
      items: response.items.map(buildMonitoringMappingJobViewModelFromUsage),
    };
  }

  async getExtractionUsage(
    filters?: ExternalRefUsageFilterParams,
  ): Promise<PaginatedResponse<MonitoringProcessViewModel>> {
    const response = await this.getMonitoringUsage("extraction", filters);
    return {
      ...response,
      items: response.items.map(buildMonitoringProcessViewModelFromUsage),
    };
  }

  async getExternalRefUsageDetail(
    externalRefType: string,
  ): Promise<ExternalRefCapabilityUsageViewModel[]> {
    const url = `/cost-events/external-ref-usage/${encodeURIComponent(externalRefType)}`;
    const response = await this.httpClient.makeAuthenticatedRequest<
      ExternalRefCapabilityUsageResponse[]
    >(url, { method: "GET" });
    return response.map(buildExternalRefCapabilityUsageViewModel);
  }

  async getCostEventsByProcessId(
    processId: string,
  ): Promise<CostEventViewModel[]> {
    const params = new URLSearchParams({
      external_ref_id: processId,
      limit: "200",
    });
    const url = `/cost-events/?${params.toString()}`;

    const response = await this.httpClient.makeAuthenticatedRequest<
      PaginatedResponse<CostEventResponse>
    >(url, { method: "GET" });
    return response.items.map(buildCostEventViewModel);
  }
}

export { CostApi };
export const costApi = new CostApi();
